import {
  CalendarPlus,
  Car,
  CarFront,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  FolderTree,
  Menu,
  MessageCircle,
  Package,
  Settings,
  Users,
  Wrench,
} from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { NavLink } from '@/components/NavLink'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useAbility } from '@/contexts/AbilityContext'
import { useIsMobile } from '@/hooks/useMobile'
import type { Subjects } from '@/libs/ability'
import { APP_ROUTES } from '@/libs/constants'
import { cn } from '@/libs/utils'

export function AppSidebar() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(false)
  const [userCollapsed, setUserCollapsed] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const isMobile = useIsMobile()

  const isSettingsPage = location.pathname.startsWith(APP_ROUTES.settings.base)

  // Auto-collapse on settings page
  useEffect(() => {
    if (isSettingsPage) {
      setCollapsed(true)
    } else {
      setCollapsed(userCollapsed)
    }
  }, [isSettingsPage, userCollapsed])

  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false)
    }
  }, [isMobile])

  const ability = useAbility()

  const allNavItems: { title: string; url: string; icon: React.ElementType; tooltipKey: string; subject: Subjects }[] = [
    {
      title: t('nav.booking'),
      url: APP_ROUTES.booking.base,
      icon: CalendarPlus,
      tooltipKey: 'tooltips.nav.booking',
      subject: 'Job',
    },
    {
      title: t('nav.customers'),
      url: APP_ROUTES.customer.base,
      icon: Users,
      tooltipKey: 'tooltips.nav.customers',
      subject: 'Customer',
    },
    {
      title: t('nav.vehicles'),
      url: APP_ROUTES.vehicle.base,
      icon: Car,
      tooltipKey: 'tooltips.nav.vehicles',
      subject: 'Vehicle',
    },
    {
      title: t('nav.jobs'),
      url: APP_ROUTES.job.base,
      icon: ClipboardList,
      tooltipKey: 'tooltips.nav.jobs',
      subject: 'Job',
    },
    {
      title: t('nav.services'),
      url: APP_ROUTES.service.base,
      icon: Wrench,
      tooltipKey: 'tooltips.nav.services',
      subject: 'Service',
    },
    {
      title: t('nav.products'),
      url: APP_ROUTES.product.base,
      icon: Package,
      tooltipKey: 'tooltips.nav.products',
      subject: 'Product',
    },
    {
      title: t('nav.categories'),
      url: APP_ROUTES.category.base,
      icon: FolderTree,
      tooltipKey: 'tooltips.nav.categories',
      subject: 'Category',
    },
    {
      title: t('nav.rentalFleet'),
      url: APP_ROUTES.rentCar.base,
      icon: CarFront,
      tooltipKey: 'tooltips.nav.rentalFleet',
      subject: 'LoanCar',
    },
    {
      title: t('nav.communication'),
      url: APP_ROUTES.communication.base,
      icon: MessageCircle,
      tooltipKey: 'tooltips.nav.communication',
      subject: 'Communication',
    },
    {
      title: t('nav.settings'),
      url: APP_ROUTES.settings.base,
      icon: Settings,
      tooltipKey: 'tooltips.nav.settings',
      subject: 'Settings',
    },
  ]

  const navItems = allNavItems.filter((item) => ability.can('read', item.subject))

  const showExpanded = !collapsed || hovered

  const SidebarContent = ({ isSheet = false }: { isSheet?: boolean }) => {
    const expanded = showExpanded || isSheet
    return (
      <>
        {/* Header */}
        <header
          className="h-12 md:h-14 px-3 md:px-4 flex items-center justify-between border-b border-sidebar-border shrink-0"
          data-tour="sidebar-logo"
        >
          <button
            onClick={() => navigate(APP_ROUTES.home)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <img src="/logo_.png" />
            {/* <div
              className="w-7 h-7 rounded-md gradient-accent flex items-center justify-center"
              aria-hidden="true"
            >
              <Car className="w-4 h-4 text-accent-foreground" />
            </div>
            {expanded && (
              <span className="font-semibold text-sm md:text-base whitespace-nowrap">
                Care of Cars
              </span>
            )} */}
          </button>
        </header>

        {/* Navigation */}
        <nav
          className="flex-1 p-1.5 md:p-2 space-y-0.5 overflow-y-auto"
          aria-label={t('nav.primaryNavigation')}
          data-tour="sidebar-nav"
        >
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.url)
            return !expanded ? (
              <Tooltip key={item.url}>
                <TooltipTrigger asChild>
                  <NavLink
                    to={item.url}
                    end={item.url === '/'}
                    onClick={() => isSheet && setMobileOpen(false)}
                    className={cn(
                      'flex items-center gap-2.5 px-2.5 py-1.5 md:py-2 rounded-md transition-all duration-200 text-[13px]',
                      'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent',
                      'justify-center',
                      isActive && 'bg-sidebar-accent text-sidebar-primary font-medium',
                    )}
                    aria-label={item.title}
                  >
                    <item.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                  </NavLink>
                </TooltipTrigger>
                <TooltipContent side="right">{t(item.tooltipKey)}</TooltipContent>
              </Tooltip>
            ) : (
              <NavLink
                key={item.url}
                to={item.url}
                end={item.url === '/'}
                onClick={() => isSheet && setMobileOpen(false)}
                className={cn(
                  'flex items-center gap-2.5 px-2.5 py-1.5 md:py-2 rounded-md transition-all duration-200 text-[13px]',
                  'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent',
                  isActive && 'bg-sidebar-accent text-sidebar-primary font-medium',
                )}
              >
                <item.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span className="whitespace-nowrap">{item.title}</span>
              </NavLink>
            )
          })}
        </nav>

        {/* Collapse toggle */}
        {!isSheet && (
          <button
            onClick={() => {
              const next = !collapsed
              setCollapsed(next)
              setUserCollapsed(next)
            }}
            className="flex items-center justify-center h-12 border-t border-sidebar-border text-sidebar-muted hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
            aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        )}
      </>
    )
  }

  // Mobile sidebar (Sheet)
  if (isMobile) {
    return (
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('nav.openMenu')}
            className={cn(
              'md:hidden absolute top-0 left-0 h-12 w-12 rounded-none border-b border-border bg-card transition-colors group',
              mobileOpen
                ? 'z-0 pointer-events-none bg-accent text-white'
                : 'z-[60] hover:bg-accent hover:text-white',
            )}
          >
            <Menu className="w-5 h-5 transition-colors text-inherit" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="left"
          className="w-[280px] p-0 bg-sidebar text-sidebar-foreground border-sidebar-border"
        >
          <SidebarContent isSheet />
        </SheetContent>
      </Sheet>
    )
  }

  // Desktop sidebar
  return (
    <TooltipProvider delayDuration={200}>
      {/* Spacer div to reserve layout space - expands on hover to push content */}
      <div
        className={cn(
          'h-screen shrink-0 hidden md:block transition-all duration-300',
          collapsed && !hovered ? 'w-14' : 'w-52 lg:w-56',
        )}
      />
      {/* Actual sidebar - fixed positioning for smooth transitions */}
      <aside
        className={cn(
          'h-screen bg-sidebar text-sidebar-foreground flex flex-col transition-all duration-300 fixed top-0 left-0 z-[60] hidden md:flex',
          collapsed && !hovered ? 'w-14' : 'w-52 lg:w-56',
        )}
        onMouseEnter={() => {
          if (collapsed) setHovered(true)
        }}
        onMouseLeave={() => {
          if (collapsed) setHovered(false)
        }}
        aria-label={t('nav.mainNavigation')}
      >
        <SidebarContent />
      </aside>
    </TooltipProvider>
  )
}
